import React from 'react';

/**
 * Initials placeholder standing in for a profile photo.
 *
 * Colour is derived from the name rather than random or sequential, so the
 * same person keeps the same colour on every screen they appear on — which is
 * what makes an initials avatar scannable at all.
 */
const PALETTE = [
  ['#4f46e5', '#818cf8'], ['#0891b2', '#22d3ee'], ['#059669', '#34d399'],
  ['#c2410c', '#fb923c'], ['#9333ea', '#c084fc'], ['#b91c1c', '#f87171'],
  ['#0369a1', '#38bdf8'], ['#a16207', '#fbbf24'],
];

export const initialsOf = (first, last) => {
  const a = (first || '').trim()[0] || '';
  const b = (last || '').trim()[0] || '';
  // Falls back to the first two letters of whichever single name exists, so a
  // one-name record still shows two characters instead of a lonely letter.
  return ((a + b) || (first || last || '?').slice(0, 2)).toUpperCase();
};

export default function Avatar({ first, last, src, size = 96, style }) {
  const initials = initialsOf(first, last);
  const seed = `${first || ''}${last || ''}`
    .split('')
    .reduce((n, c) => n + c.charCodeAt(0), 0);
  const [dark, light] = PALETTE[seed % PALETTE.length];

  if (src) {
    return (
      <img
        src={src}
        alt={initials}
        style={{
          width: size, height: size, borderRadius: '50%', objectFit: 'cover',
          border: '3px solid var(--border)', flexShrink: 0, ...style,
        }}
      />
    );
  }

  return (
    <div
      // The initials are decorative next to the name they sit beside; the
      // title is what a screen reader and a hover both get.
      title={[first, last].filter(Boolean).join(' ')}
      style={{
        width: size, height: size, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `linear-gradient(135deg, ${dark} 0%, ${light} 100%)`,
        color: '#fff',
        fontSize: size * 0.36, fontWeight: 800, letterSpacing: '0.02em',
        boxShadow: `0 6px 18px color-mix(in srgb, ${dark} 40%, transparent)`,
        border: '3px solid var(--bg-card)',
        userSelect: 'none',
        ...style,
      }}
    >
      {initials}
    </div>
  );
}
